// دیالوگ‌های SCR-STAFF-903: ایجاد و ویرایش شعبه (US-PLT-010) و تغییر نوع مالکیت با تاریخ مؤثر آینده (US-PLT-012).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  ErrorCodes,
  isApiError,
  unwrap,
  type BranchRequest,
  type BranchView,
  type OwnershipType,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DEFAULT_TIME_ZONE,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  SelectRHF,
  supportedTimeZones,
  TextFieldRHF,
  toBusinessDate,
  UpgradeBadge,
  zBusinessDate,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import { SUBSCRIPTION_PATH } from '../../navigation';
import { pltKeys, type GeoDivisions } from './common';

export const OWNERSHIP_TYPES: readonly OwnershipType[] = [
  'Private',
  'Governmental',
  'Charity',
  'PublicNonGovernmental',
];

const branchSchema = z.object({
  code: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9-]{0,15}$/, { error: 'errors:validation.branch_code' }),
  name: zRequiredText(200),
  provinceCode: z.string(),
  cityCode: z.string(),
  address: z.string().max(500, { error: 'common:validation.too_long' }),
  phone: z.string().max(32, { error: 'common:validation.too_long' }),
  timeZone: z.string().min(1, { error: 'common:validation.required' }),
  ownershipType: z.enum(['', 'Private', 'Governmental', 'Charity', 'PublicNonGovernmental']),
});
type BranchForm = z.input<typeof branchSchema>;

const BRANCH_FIELDS = [
  'code',
  'name',
  'provinceCode',
  'cityCode',
  'address',
  'phone',
  'timeZone',
  'ownershipType',
] as const;

/** هشدار سقف پلن با UpgradeBadge و پیشنهاد ارتقا (AC-PLT-010.1). */
export function LimitReachedAlert({ error }: { error: unknown }) {
  const { t } = useTranslation('plt');
  if (!isApiError(error) || error.code !== ErrorCodes.limitReached) {
    return null;
  }
  return (
    <Alert
      severity="warning"
      data-testid="branch-limit-reached"
      data-error-code={error.code}
      icon={false}
      action={
        <Button
          component={RouterLink}
          to={SUBSCRIPTION_PATH}
          color="inherit"
          data-testid="branch-limit-upgrade"
        >
          {t('common:actions.upgrade')}
        </Button>
      }
    >
      <AlertTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t('errors:error.limit_reached')}</span>
          <UpgradeBadge feature="limit.branches" to={SUBSCRIPTION_PATH} />
        </Stack>
      </AlertTitle>
      {t('plt:branches.limit_reached')}
    </Alert>
  );
}

export function BranchFormDialog({
  open,
  branch,
  geo,
  onClose,
}: {
  open: boolean;
  /** null ← ایجاد. */
  branch: BranchView | null;
  geo: GeoDivisions;
  onClose: () => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = branch === null;

  const values = useMemo<BranchForm>(
    () => ({
      code: branch?.code ?? '',
      name: branch?.name ?? '',
      provinceCode: branch?.provinceCode ?? '',
      cityCode: branch?.cityCode ?? '',
      address: branch?.address ?? '',
      phone: branch?.phone ?? '',
      timeZone: branch?.timeZone ?? DEFAULT_TIME_ZONE,
      ownershipType: '',
    }),
    [branch],
  );
  const { control, handleSubmit, formState, setError, reset, setValue } = useForm<BranchForm>({
    resolver: zodResolver(branchSchema),
    values,
  });
  const province = useWatch({ control, name: 'provinceCode' });
  const city = useWatch({ control, name: 'cityCode' });
  const cities = geo.citiesOf(province);
  useEffect(() => {
    if (city && !cities.some((item) => item.key === city)) {
      setValue('cityCode', '', { shouldDirty: true });
    }
  }, [city, cities, setValue]);

  const save = useMutation({
    mutationFn: (body: BranchRequest) =>
      creating
        ? unwrap(
            tenantApi.POST('/api/v1/platform/branches', {
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/platform/branches/{id}', { params: { path: { id: branch.id } }, body }),
          ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pltKeys.branches(session?.tenantId) });
      access.refetch();
      reset();
      onClose();
    },
    onError: (error) => {
      let applied = applyServerErrors(error, setError, {
        fields: BRANCH_FIELDS,
        aliases: { province_code: 'provinceCode', city_code: 'cityCode' },
        codeFields: { 'PHX-PLT-0301': 'code' },
      });
      if (isApiError(error) && error.code === 'PHX-PLT-0303') {
        setError(error.extensions['field'] === 'city_code' ? 'cityCode' : 'provinceCode', {
          type: 'server',
          message: 'errors:error.geo_division_invalid',
        });
        applied += 1;
      }
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = handleSubmit((form) =>
    save.mutate({
      code: form.code.trim(),
      name: form.name,
      provinceCode: form.provinceCode || null,
      cityCode: form.cityCode || null,
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      timeZone: form.timeZone,
      ownershipType: creating && form.ownershipType ? form.ownershipType : null,
      rowVersion: creating ? null : branch.rowVersion,
    }),
  );

  const limitError = isApiError(submitError) && submitError.code === ErrorCodes.limitReached;

  return (
    <FormDialog
      open={open}
      screenId="SCR-STAFF-903-FORM"
      titleKey={creating ? 'plt:branches.create_title' : 'plt:branches.edit_title'}
      onSubmit={() => void submit()}
      onClose={() => {
        reset();
        setSubmitError(undefined);
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={limitError ? undefined : submitError}
    >
      <LimitReachedAlert error={submitError} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="plt:branches.form.code"
          helperKey={creating ? 'plt:branches.form.code_help' : 'plt:branches.form.code_immutable'}
          required
          fullWidth
          disabled={!creating}
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'branch-code' } }}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="plt:branches.form.name"
          required
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'branch-name' } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="provinceCode"
          labelKey="plt:branches.form.province"
          fullWidth
          options={[
            { value: '', labelKey: 'plt:branches.form.none' },
            ...geo.provinces.map((p) => ({ value: p.key, label: p.name })),
          ]}
        />
        <SelectRHF
          control={control}
          name="cityCode"
          labelKey="plt:branches.form.city"
          fullWidth
          disabled={!province}
          options={[
            { value: '', labelKey: 'plt:branches.form.none' },
            ...cities.map((c) => ({ value: c.key, label: c.name })),
          ]}
        />
      </Stack>
      <TextFieldRHF
        control={control}
        name="address"
        labelKey="plt:branches.form.address"
        fullWidth
        multiline
        minRows={2}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="phone"
          labelKey="plt:branches.form.phone"
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel' } }}
        />
        <SelectRHF
          control={control}
          name="timeZone"
          labelKey="plt:branches.form.time_zone"
          required
          fullWidth
          options={supportedTimeZones(branch?.timeZone).map((zone) => ({ value: zone, label: zone }))}
        />
      </Stack>
      {creating && (
        <SelectRHF
          control={control}
          name="ownershipType"
          labelKey="plt:branches.form.ownership_type"
          helperKey="plt:branches.form.ownership_default_help"
          fullWidth
          options={[
            { value: '', labelKey: 'plt:branches.form.ownership_default' },
            ...OWNERSHIP_TYPES.map((value) => ({ value, labelKey: `plt:ownership_type.${value}` })),
          ]}
        />
      )}
      {!creating && (
        <Typography variant="caption" color="text.secondary">
          {t('plt:branches.form.ownership_edit_hint')}
        </Typography>
      )}
    </FormDialog>
  );
}

/** فردای امروز (تاریخ مؤثر باید پس از امروز شعبه باشد — C-AGENT-156). */
function tomorrow(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return toBusinessDate(date);
}

const ownershipSchema = z
  .object({
    ownershipType: z.enum(['Private', 'Governmental', 'Charity', 'PublicNonGovernmental'], {
      error: 'common:validation.required',
    }),
    effectiveFrom: zBusinessDate(),
  })
  .superRefine((value, ctx) => {
    if (value.effectiveFrom && value.effectiveFrom < tomorrow()) {
      ctx.addIssue({
        code: 'custom',
        path: ['effectiveFrom'],
        message: 'errors:error.branch_ownership_retroactive',
      });
    }
  });
type OwnershipForm = z.input<typeof ownershipSchema>;

export function OwnershipDialog({
  branch,
  canWrite,
  onClose,
}: {
  branch: BranchView | null;
  canWrite: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError, reset } = useForm<OwnershipForm>({
    resolver: zodResolver(ownershipSchema),
    defaultValues: { ownershipType: undefined, effectiveFrom: undefined },
  });

  const change = useMutation({
    mutationFn: (form: { ownershipType: OwnershipType; effectiveFrom: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/platform/branches/{id}/ownership-types', {
          params: { path: { id: branch?.id ?? '' } },
          body: form,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pltKeys.branches(session?.tenantId) });
      reset();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['ownershipType', 'effectiveFrom'],
        codeFields: { 'PHX-PLT-0302': 'effectiveFrom', 'PHX-GEN-0003': 'effectiveFrom' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const history = [...(branch?.ownership ?? [])].sort((a, b) =>
    b.effectiveFrom.localeCompare(a.effectiveFrom),
  );

  return (
    <FormDialog
      open={branch !== null}
      screenId="SCR-STAFF-903-OWNERSHIP"
      titleKey="plt:branches.ownership.title"
      onSubmit={() => {
        if (canWrite) {
          void handleSubmit((form) =>
            change.mutate(form as { ownershipType: OwnershipType; effectiveFrom: string }),
          )();
        }
      }}
      onClose={() => {
        reset();
        setSubmitError(undefined);
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={change.isPending}
      submitError={submitError}
      submitKey="plt:branches.ownership.submit"
    >
      <Typography variant="subtitle2">
        {t('plt:branches.ownership.history', { name: branch?.name ?? '' })}
      </Typography>
      <Table
        size="small"
        aria-label={t('plt:branches.ownership.history', { name: branch?.name ?? '' })}
        data-testid="ownership-history"
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('plt:branches.ownership.type')}</TableCell>
            <TableCell>{t('plt:branches.ownership.effective_from')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((period) => (
            <TableRow key={period.effectiveFrom}>
              <TableCell>{t(`plt:ownership_type.${period.ownershipType}`)}</TableCell>
              <TableCell>
                <JalaliDate value={period.effectiveFrom} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canWrite && (
        <>
          <Typography variant="subtitle2">{t('plt:branches.ownership.add')}</Typography>
          <SelectRHF
            control={control}
            name="ownershipType"
            labelKey="plt:branches.ownership.type"
            required
            fullWidth
            options={OWNERSHIP_TYPES.map((value) => ({ value, labelKey: `plt:ownership_type.${value}` }))}
          />
          <JalaliDatePickerRHF
            control={control}
            name="effectiveFrom"
            labelKey="plt:branches.ownership.effective_from"
            helperKey="plt:branches.ownership.future_only"
            required
          />
        </>
      )}
    </FormDialog>
  );
}
